import {
  BLEND_NORMAL,
  LAYER_KIND_RASTER,
  LAYER_KIND_TEXT,
  createEditorLayer,
} from './imageEditorDocument.js';
import {
  cloneEditorTextLayerContent,
  isEditorTextLayer,
} from './imageEditorTextLayer.js';

export const IMAGE_EDITOR_PROJECT_FORMAT = 'ieproj';
export const IMAGE_EDITOR_PROJECT_MIME = 'application/x-shifty-ie-project+json';
export const IMAGE_EDITOR_PROJECT_KIND = 'layered';
/**
 * Manifest v3: live text layers with runs[] (rich spans) + block defaults.
 * v2 flat params migrate to one synthetic run on load/clone.
 * v1 raster-only path unchanged.
 */
export const IMAGE_EDITOR_PROJECT_VERSION = 3;

/**
 * @param {string} documentId
 * @returns {string}
 */
export function projectStoragePrefix(documentId) {
  const safe = String(documentId || 'doc')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return `ieproj_${safe || 'doc'}`;
}

/**
 * @param {string} documentId
 * @returns {string}
 */
export function projectPreviewFileName(documentId) {
  return `${projectStoragePrefix(documentId)}__preview.png`;
}

/**
 * @param {string} documentId
 * @param {string} layerId
 * @returns {string}
 */
export function projectLayerFileName(documentId, layerId) {
  const safeLayer = String(layerId || 'layer')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return `${projectStoragePrefix(documentId)}__layer_${safeLayer || 'layer'}.png`;
}

/**
 * @param {import('./imageEditorDocument').EditorLayer} layer
 * @param {number} index
 * @param {Record<string, string>} layerStoragePathsById
 */
function serializeManifestLayer(layer, index, layerStoragePathsById) {
  const base = {
    id: layer.id,
    order: index,
    name: layer.name,
    visible: layer.visible !== false,
    opacity:
      typeof layer.opacity === 'number'
        ? Math.max(0, Math.min(1, layer.opacity))
        : 1,
    blendMode:
      typeof layer.blendMode === 'string' ? layer.blendMode : BLEND_NORMAL,
  };
  if (isEditorTextLayer(layer)) {
    return {
      ...base,
      kind: LAYER_KIND_TEXT,
      text: cloneEditorTextLayerContent(layer.text),
      storagePath: null,
    };
  }
  return {
    ...base,
    kind: LAYER_KIND_RASTER,
    storagePath: layerStoragePathsById[layer.id] || null,
  };
}

/**
 * @param {import('./imageEditorDocument').EditorDocument} doc
 * @param {{
 *   revision: number,
 *   persistedRevision?: number,
 *   autosave?: boolean,
 *   layerStoragePathsById?: Record<string, string>,
 *   previewStoragePath?: string|null,
 * }} opts
 */
export function buildLayeredProjectManifest(doc, opts) {
  const layerStoragePathsById = opts.layerStoragePathsById || {};
  return {
    version: IMAGE_EDITOR_PROJECT_VERSION,
    documentId: doc.id,
    width: doc.width,
    height: doc.height,
    activeLayerId: doc.activeLayerId,
    revision: Number(opts.revision) || 0,
    persistedRevision:
      Number.isFinite(opts.persistedRevision) ? Number(opts.persistedRevision) : 0,
    autosave: Boolean(opts.autosave),
    layerCount: doc.layers.length,
    previewStoragePath:
      typeof opts.previewStoragePath === 'string' ? opts.previewStoragePath : null,
    layers: doc.layers.map((layer, index) =>
      serializeManifestLayer(layer, index, layerStoragePathsById),
    ),
  };
}

/**
 * @param {ImageData} imageData
 * @returns {ImageData}
 */
function cloneImageData(imageData) {
  return new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height,
  );
}

/**
 * @param {unknown} row
 * @returns {boolean}
 */
function manifestRowIsTextLayer(row) {
  return Boolean(
    row &&
      typeof row === 'object' &&
      row.kind === LAYER_KIND_TEXT &&
      row.text &&
      typeof row.text === 'object',
  );
}

/**
 * Rehydrate a layered project. v3/v2 text layers restore live params (+ runs);
 * v2 flat params become one synthetic run via cloneEditorTextLayerContent.
 * v1 and raster rows load from PNG blobs as kind:'raster'.
 *
 * @param {{
 *   documentId?: string,
 *   width: number,
 *   height: number,
 *   activeLayerId?: string,
 *   revision?: number,
 *   persistedRevision?: number,
 *   version?: number,
 *   layers: Array<{
 *     id: string,
 *     name?: string,
 *     visible?: boolean,
 *     opacity?: number,
 *     blendMode?: string,
 *     kind?: string,
 *     text?: object,
 *     storagePath?: string|null,
 *   }>,
 * }} manifest
 * @param {Record<string, ImageData>} layerImageDataById
 * @returns {import('./imageEditorDocument').EditorDocument}
 */
export function rehydrateLayeredProjectManifest(manifest, layerImageDataById) {
  const width = Math.max(1, Math.round(Number(manifest?.width) || 1));
  const height = Math.max(1, Math.round(Number(manifest?.height) || 1));
  const layerRows = Array.isArray(manifest?.layers) ? manifest.layers : [];
  if (!layerRows.length) {
    throw new Error('Layered project has no layers.');
  }
  const layers = layerRows.map((row, index) => {
    const id = row.id || `layer_${index}`;
    const name =
      typeof row.name === 'string' && row.name.trim()
        ? row.name.trim()
        : `Layer ${index + 1}`;
    const visible = row.visible !== false;
    const opacity =
      typeof row.opacity === 'number'
        ? Math.max(0, Math.min(1, row.opacity))
        : 1;
    const blendMode =
      typeof row.blendMode === 'string' ? row.blendMode : BLEND_NORMAL;

    if (manifestRowIsTextLayer(row)) {
      return createEditorLayer({
        id,
        name,
        visible,
        opacity,
        blendMode,
        pixels: new ImageData(width, height),
        kind: LAYER_KIND_TEXT,
        text: cloneEditorTextLayerContent(row.text),
      });
    }

    const pixels = layerImageDataById[id];
    if (!pixels) throw new Error(`Missing layer pixels for ${id}`);
    if (pixels.width !== width || pixels.height !== height) {
      throw new Error(`Layer ${id} size does not match manifest dimensions.`);
    }
    return createEditorLayer({
      id,
      name,
      visible,
      opacity,
      blendMode,
      pixels: cloneImageData(pixels),
      kind: LAYER_KIND_RASTER,
    });
  });
  const activeLayerId =
    typeof manifest?.activeLayerId === 'string' &&
    layers.some((l) => l.id === manifest.activeLayerId)
      ? manifest.activeLayerId
      : layers[layers.length - 1].id;
  return {
    id:
      typeof manifest?.documentId === 'string' && manifest.documentId
        ? manifest.documentId
        : `iedoc_${Date.now().toString(36)}`,
    width,
    height,
    layers,
    activeLayerId,
    revision: Number.isFinite(manifest?.revision) ? Number(manifest.revision) : 0,
    persistedRevision: Number.isFinite(manifest?.persistedRevision)
      ? Number(manifest.persistedRevision)
      : 0,
    compositeDirty: true,
    cowUniqueLayerIds: new Set(layers.map((l) => l.id)),
  };
}
